if (!customElements.get('adaptable-sale-bestsellers')) {
  class AdaptableSaleBestsellers extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;

      this.track = this.querySelector('[data-asbs-track]');
      this.slides = Array.from(this.querySelectorAll('[data-asbs-slide]'));
      this.previousButton = this.querySelector('[data-asbs-previous]');
      this.nextButton = this.querySelector('[data-asbs-next]');
      this.autoplayButton = this.querySelector('[data-asbs-autoplay]');
      this.progressBar = this.querySelector('[data-asbs-progress-bar]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayRequested = this.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5000;
      this.scrollStep = Number(this.dataset.scrollStep) || 1;
      this.userPaused = false;
      this.interactionPaused = false;
      this.updateFrame = null;
      this.autoplayTimer = null;

      if (!this.track || this.slides.length === 0) return;

      this.onScroll = this.scheduleUpdate.bind(this);
      this.onPrevious = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.onAutoplayToggle = this.toggleAutoplay.bind(this);
      this.onPointerEnter = () => this.setInteractionPaused(true);
      this.onPointerLeave = () => this.setInteractionPaused(false);
      this.onFocusIn = () => this.setInteractionPaused(true);
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.setInteractionPaused(false);
      };
      this.onVisibilityChange = () => this.refreshAutoplay();
      this.onMotionChange = () => {
        this.toggleAttribute('data-reduced-motion', this.reducedMotion.matches);
        this.refreshAutoplay();
      };

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.autoplayButton?.addEventListener('click', this.onAutoplayToggle);
      this.addEventListener('mouseenter', this.onPointerEnter);
      this.addEventListener('mouseleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.reducedMotion.addEventListener('change', this.onMotionChange);

      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(this.track);

      this.dataset.ready = 'true';
      this.toggleAttribute('data-reduced-motion', this.reducedMotion.matches);
      this.scheduleUpdate();
      this.refreshAutoplay();
    }

    disconnectedCallback() {
      if (!this.track) return;

      this.track.removeEventListener('scroll', this.onScroll);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.autoplayButton?.removeEventListener('click', this.onAutoplayToggle);
      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.reducedMotion?.removeEventListener('change', this.onMotionChange);
      this.resizeObserver?.disconnect();
      this.stopAutoplay();

      if (this.updateFrame) window.cancelAnimationFrame(this.updateFrame);
    }

    scheduleUpdate() {
      if (this.updateFrame) return;

      this.updateFrame = window.requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    }

    update() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.min(maxScroll, Math.max(0, this.track.scrollLeft));
      const isStatic = maxScroll <= 1;
      const wasStatic = this.hasAttribute('data-static');

      this.toggleAttribute('data-static', isStatic);
      this.setControlState(this.previousButton, isStatic || position <= 1);
      this.setControlState(this.nextButton, isStatic || position >= maxScroll - 1);

      if (this.progressBar) {
        const visibleWidth = isStatic ? 100 : Math.max(8, (this.track.clientWidth / this.track.scrollWidth) * 100);
        const progressPosition = isStatic ? 0 : (position / maxScroll) * (100 - visibleWidth);
        this.progressBar.style.width = `${visibleWidth}%`;
        this.progressBar.style.marginInlineStart = `${progressPosition}%`;
      }

      if (isStatic) this.stopAutoplay();
      if (wasStatic !== isStatic) this.refreshAutoplay();
    }

    setControlState(button, unavailable) {
      if (!button) return;
      button.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
    }

    getStepSize() {
      const firstSlide = this.slides[0];
      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return (firstSlide.getBoundingClientRect().width + gap) * this.scrollStep;
    }

    move(direction) {
      const button = direction < 0 ? this.previousButton : this.nextButton;
      if (button?.getAttribute('aria-disabled') === 'true') return;

      this.track.scrollBy({
        left: this.getStepSize() * direction,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.updateAutoplayButton();
      this.refreshAutoplay();
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;

      this.autoplayButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      this.autoplayButton.setAttribute(
        'aria-label',
        this.userPaused ? this.autoplayButton.dataset.playLabel : this.autoplayButton.dataset.pauseLabel
      );
    }

    setInteractionPaused(paused) {
      this.interactionPaused = paused;
      this.refreshAutoplay();
    }

    refreshAutoplay() {
      const shouldRun =
        this.autoplayRequested &&
        !this.reducedMotion.matches &&
        !this.userPaused &&
        !this.interactionPaused &&
        !document.hidden &&
        !this.hasAttribute('data-static');

      if (shouldRun) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    startAutoplay() {
      if (this.autoplayTimer) return;

      this.autoplayTimer = window.setInterval(() => {
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const atEnd = this.track.scrollLeft >= maxScroll - 1;

        if (atEnd) {
          this.track.scrollTo({ left: 0, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
        } else {
          this.track.scrollBy({ left: this.getStepSize(), behavior: 'smooth' });
        }
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define('adaptable-sale-bestsellers', AdaptableSaleBestsellers);
}
